import { createApi } from '@reduxjs/toolkit/query/react';
import { getKsdmConfig } from 'helpers/index';
import { baseQuery } from './apiConfig';

/**
 * RTK Query api for the "Report a Bug" form in the global header
 * (`pages/dashboard/components/header/BugReportPopup.tsx`).
 *
 * One endpoint: POST /api/v1.0/bug-reports/ (`django/api/rest_bug_report/`).
 * The server hands the report to the central bug-report pipeline (S3 + SQS; see
 * `django/bug_report/intake.py`), which stores it and emails support. A 202
 * means the report is queued and safe; a 503 means it was not, and the popup
 * keeps the form so the reporter can retry.
 *
 * Sent as multipart/form-data always, not only when there is a file: one shape
 * for the request keeps the serializer's parser choice irrelevant and avoids a
 * second code path that is only exercised by the no-attachment case.
 * `fetchBaseQuery` leaves FormData alone (no JSON stringify, no Content-Type
 * header — the browser sets the multipart boundary).
 */

/** Values mirror `SEVERITIES` (django/bug_report/intake.py). */
export const BUG_SEVERITIES = [
  { value: 'blocker', label: "Blocker — I can't continue working" },
  { value: 'major', label: 'Major — wrong results or a broken feature' },
  { value: 'minor', label: 'Minor — there is a workaround' },
  { value: 'cosmetic', label: 'Cosmetic — wording or layout' },
] as const;

/**
 * Values mirror `AREAS` (django/bug_report/intake.py). They ARE the SPA's `appName`s
 * (GlobalHeader's prop), which is what lets the form pre-select the page the
 * reporter is on without a translation table.
 */
export const BUG_AREAS = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'project', label: 'Project Board' },
  { value: 'ahu', label: 'AHU Board' },
  { value: 'report', label: 'Reports' },
  { value: 'sizer', label: 'AHU Sizer' },
  { value: 'kai-select', label: 'KAI Select' },
  { value: 'partners', label: 'Partners' },
  { value: 'user_settings', label: 'User Settings' },
  { value: 'cms', label: 'CMS Admin' },
  { value: 'cad', label: 'CAD Viewer' },
  { value: 'other', label: 'Something else' },
] as const;

/** Server-side minimums (api/rest_bug_report/serializers.py), mirrored so the
 *  Submit button can be blocked before a round trip. */
export const MIN_SUBJECT_LENGTH = 5;
export const MIN_DESCRIPTION_LENGTH = 15;
export const MAX_SUBJECT_LENGTH = 200;

/**
 * This server's attachment rules, from `__KSDM_CONFIG__.bugReport`
 * (sdmk/react_views.py). NOT mirrored constants: both caps are per-server env
 * vars, so a hardcoded copy would refuse uploads a tenant had chosen to allow.
 * The fallbacks are only for a page served before that key existed.
 */
export const bugAttachmentRules = () => {
  const cfg = getKsdmConfig()?.bugReport;
  return {
    maxAttachmentBytes: cfg?.maxAttachmentBytes ?? 5 * 1024 * 1024,
    maxVideoBytes: cfg?.maxVideoBytes ?? 50 * 1024 * 1024,
    videoExtensions: cfg?.videoExtensions ?? ['.mp4', '.mov', '.webm', '.m4v', '.mkv'],
    extensions: cfg?.extensions ?? [],
  };
};

const mb = (bytes: number) => Math.round(bytes / 1048576);

/**
 * The reason a picked file can't be sent, or null if it can.
 *
 * Checked in the browser because the alternative is uploading the whole thing
 * first: a 60 MB recording is killed by nginx's `client_max_body_size` with an
 * HTML 413 the SPA can't turn into a sentence, and anything between the cap and
 * that limit is pushed in full only to be refused by the serializer. The server
 * still enforces both rules — this exists so the reporter hears about it in the
 * moment they pick the file.
 */
export const attachmentError = (file: File): string | null => {
  const rules = bugAttachmentRules();
  const name = file.name.toLowerCase();
  const isVideo = rules.videoExtensions.some((ext) => name.endsWith(ext));
  if (rules.extensions.length && !rules.extensions.some((ext) => name.endsWith(ext))) {
    return 'Attach an image, screen recording, PDF, text/log/CSV file or a zip.';
  }
  const limit = isVideo ? rules.maxVideoBytes : rules.maxAttachmentBytes;
  if (file.size > limit) {
    return `That file is ${(file.size / 1048576).toFixed(1)} MB. The limit is ${mb(limit)} MB${
      isVideo ? '' : `, or ${mb(rules.maxVideoBytes)} MB for a screen recording`
    }.`;
  }
  return null;
};

/** What the reporter typed. Everything else is collected automatically. */
export interface IBugReportDraft {
  subject: string;
  description: string;
  steps: string;
  severity: string;
  area: string;
  attachment?: File | null;
}

export interface IBugReportCreated {
  /** UUID of the report in the central store. */
  id: string;
  /** Short form of `id` for the reporter to quote, e.g. "3F9A1C2B". */
  reference: string;
  created_at: string;
}

/** `/project/<id>` and `/ahu/detail/<id>` (App.tsx routes), else null. */
const idFromPath = (pattern: RegExp): number | null => {
  const m = window.location.pathname.match(pattern);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
};

/**
 * The context the reporter should not have to describe: where they were, in
 * what, and on which build. Kept here rather than in the popup so there is one
 * place to read when asking "what does a bug report actually tell us?".
 *
 * Only scalars — the server drops anything else from `client_context`.
 */
const collectContext = (): Record<string, string | number> => {
  const cfg = getKsdmConfig();
  const ctx: Record<string, string | number> = {
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
    devicePixelRatio: window.devicePixelRatio ?? 1,
    language: navigator.language ?? '',
    platform: navigator.platform ?? '',
  };
  try {
    ctx.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    // Intl is present everywhere we support; a missing timezone is not worth
    // failing a bug report over.
  }
  if (cfg?.environment) ctx.environment = cfg.environment;
  if (cfg?.versions?.software) ctx.softwareVersion = cfg.versions.software;
  if (cfg?.versions?.master) ctx.masterVersion = cfg.versions.master;
  if (cfg?.versions?.cms) ctx.cmsVersion = cfg.versions.cms;
  if (document.referrer) ctx.referrer = document.referrer.slice(0, 500);
  return ctx;
};

const toFormData = (draft: IBugReportDraft): FormData => {
  const body = new FormData();
  body.append('subject', draft.subject.trim());
  body.append('description', draft.description.trim());
  body.append('steps', draft.steps.trim());
  body.append('severity', draft.severity);
  body.append('area', draft.area);
  body.append('page_url', window.location.href);
  body.append('user_agent', navigator.userAgent ?? '');
  const projectId = idFromPath(/^\/project\/(\d+)/);
  const ahuId = idFromPath(/^\/ahu\/detail\/(\d+)/);
  if (projectId !== null) body.append('project_id', String(projectId));
  if (ahuId !== null) body.append('ahu_id', String(ahuId));
  body.append('client_context', JSON.stringify(collectContext()));
  if (draft.attachment) body.append('attachment', draft.attachment);
  return body;
};

export const bugReportApi = createApi({
  reducerPath: 'bugReportApi',
  baseQuery,
  endpoints: (builder) => ({
    createBugReport: builder.mutation<IBugReportCreated, IBugReportDraft>({
      query: (draft) => ({
        url: '/api/v1.0/bug-reports/',
        method: 'POST',
        body: toFormData(draft),
      }),
    }),
  }),
});

export const { useCreateBugReportMutation } = bugReportApi;
